import React from 'react';

class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { 
            hasError: false, 
            error: null,
            errorInfo: null 
        };
    }

    static getDerivedStateFromError(error) {
        // Update state so the next render will show the fallback UI
        return { hasError: true };
    }

    componentDidCatch(error, errorInfo) {
        // Log error details
        console.error('ErrorBoundary caught an error:', error, errorInfo);
        this.setState({
            error: error,
            errorInfo: errorInfo
        });
    }

    handleRestart = () => {
        // Reset error state and refresh
        this.setState({ 
            hasError: false, 
            error: null, 
            errorInfo: null 
        });
        
        // If there's a restart function passed as prop, use it
        if (this.props.onRestart) {
            this.props.onRestart();
        }
    };

    render() {
        if (this.state.hasError) {
            return (
                <div className="error-boundary-container">
                    <div className="error-boundary-content paper-bg paper-border p-xl text-center">
                        <h1 className="font-impact" style={{ color: 'var(--color-error)', fontSize: '2.5rem', marginBottom: 'var(--spacing-lg)' }}>
                            Oops! Something went wrong! 🤖
                        </h1>
                        
                        <div style={{ marginBottom: 'var(--spacing-xl)' }}>
                            <p className="font-typewriter" style={{ fontSize: '1.2rem', marginBottom: 'var(--spacing-md)' }}>
                                Don't worry - this happens sometimes in the digital world!
                            </p>
                            <p className="font-typewriter" style={{ color: '#666', marginBottom: 'var(--spacing-lg)' }}>
                                The Mad Libs generator encountered an unexpected error.
                            </p>
                        </div>

                        <div className="flex-center" style={{ gap: 'var(--spacing-md)', marginBottom: 'var(--spacing-xl)' }}>
                            <button 
                                onClick={this.handleRestart}
                                className="btn btn-primary"
                                style={{ fontSize: '1rem' }}
                            >
                                🔄 Try Again
                            </button>
                            
                            <button 
                                onClick={() => window.location.reload()}
                                className="btn btn-secondary"
                                style={{ fontSize: '1rem' }}
                            >
                                🏠 Start Over
                            </button>
                        </div>

                        {process.env.NODE_ENV === 'development' && this.state.error && (
                            <details style={{ marginTop: 'var(--spacing-lg)', textAlign: 'left' }}>
                                <summary style={{ cursor: 'pointer', marginBottom: 'var(--spacing-sm)' }}>
                                    🔧 Developer Info (Development Mode)
                                </summary>
                                <div style={{ 
                                    background: '#f5f5f5', 
                                    padding: 'var(--spacing-md)', 
                                    borderRadius: 'var(--radius-sm)',
                                    fontFamily: 'monospace',
                                    fontSize: '0.8rem',
                                    overflow: 'auto'
                                }}>
                                    <strong>Error:</strong> {this.state.error.toString()}
                                    <br />
                                    <strong>Stack Trace:</strong>
                                    <pre style={{ whiteSpace: 'pre-wrap', marginTop: 'var(--spacing-sm)' }}>
                                        {this.state.errorInfo.componentStack}
                                    </pre>
                                </div>
                            </details>
                        )}
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

// CSS for error boundary (inline to avoid dependency issues)
const style = document.createElement('style');
style.textContent = `
.error-boundary-container {
    min-height: 100vh;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: var(--spacing-lg);
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.error-boundary-content {
    max-width: 600px;
    width: 100%;
    position: relative;
}

.error-boundary-content::before {
    content: '';
    position: absolute;
    top: -10px;
    left: -10px;
    right: -10px;
    bottom: -10px;
    background: linear-gradient(45deg, #ff6b00, #7c3aed, #059669, #d97706);
    border-radius: var(--radius-lg);
    z-index: -1;
    animation: rainbow-border 3s ease-in-out infinite;
}

@keyframes rainbow-border {
    0%, 100% { filter: hue-rotate(0deg); }
    50% { filter: hue-rotate(180deg); }
}
`;
document.head.appendChild(style);

export default ErrorBoundary;